import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, ShieldCheck, KeyRound, UserCheck, UserX, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  name: "", email: "", password: "", role: "site_engineer",
  phone: "", employee_code: "", department: "", designation: "", is_active: true, template_id: "",
};

const roleColor = (r) => ({
  super_admin: "bg-primary/15 text-primary border-primary/30",
  director: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  hr: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  accountant: "bg-sky-500/15 text-sky-500 border-sky-500/30",
  client: "bg-muted text-muted-foreground border-border",
}[r] || "bg-muted text-muted-foreground border-border");

export default function UserManagement() {
  const { user: currentUser, refresh } = useAuth();
  const isSuper = currentUser?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [schema, setSchema] = useState({ modules: [], actions: [], roles: [], role_defaults: {} });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [permDlg, setPermDlg] = useState(null); // user being edited for permissions
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/users"), api.get("/permissions/schema")];
      if (isSuper) reqs.push(api.get("/permission-templates").catch(() => ({ data: [] })));
      const [u, s, t] = await Promise.all(reqs);
      setUsers(u.data); setSchema(s.data); if (t) setTemplates(t.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setEditId(null); setDlgOpen(true); };
  const openEdit = (u) => {
    setForm({
      name: u.name || "", email: u.email || "", password: "",
      role: u.role, phone: u.phone || "", employee_code: u.employee_code || "",
      department: u.department || "", designation: u.designation || "",
      is_active: u.is_active !== false,
    });
    setEditId(u.id); setDlgOpen(true);
  };

  const save = async () => {
    try {
      if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email required");
      if (!editId && !form.password) return toast.error("Set an initial password");
      if (editId) {
        const patch = { ...form };
        if (!patch.password) delete patch.password;
        await api.patch(`/users/${editId}`, patch);
        toast.success("User updated");
      } else {
        const body = { ...form };
        if (!body.template_id) delete body.template_id;
        await api.post("/users", body);
        toast.success("User created — share the initial password securely");
      }
      setDlgOpen(false); setForm(emptyForm); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const toggleActive = async (u) => {
    try {
      const path = u.is_active === false ? "activate" : "deactivate";
      await api.post(`/users/${u.id}/${path}`);
      toast.success(u.is_active === false ? "User activated" : "User deactivated");
      load();
      if (u.id === currentUser?.id) refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user "${u.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Access Control
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">User Management</h1>
          <p className="text-sm text-muted-foreground mt-2">Create accounts, assign roles, and grant fine-grained module permissions.</p>
        </div>
        <Button data-testid="new-user-btn" onClick={openNew} className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" />New User</Button>
      </div>

      <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            <tr>
              <th className="text-left py-3 px-4">User</th>
              <th className="text-left">Role</th>
              <th className="text-left">Employee</th>
              <th className="text-left">Contact</th>
              <th className="text-left">Status</th>
              <th className="text-right pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</td></tr>}
            {!loading && users.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No users yet.</td></tr>}
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-border/50 hover:bg-muted/20">
                <td className="py-3 px-4">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </td>
                <td>
                  <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border ${roleColor(u.role)}`}>{(u.role || "").replace(/_/g," ")}</span>
                </td>
                <td className="text-xs">
                  {u.employee_code && <div className="font-mono">{u.employee_code}</div>}
                  <div className="text-muted-foreground">{[u.department, u.designation].filter(Boolean).join(" · ") || "—"}</div>
                </td>
                <td className="text-xs text-muted-foreground">{u.phone || "—"}</td>
                <td>
                  {u.is_active === false
                    ? <span data-testid={`user-status-${u.id}`} className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border border-rose-500/30 bg-rose-500/15 text-rose-500">Deactivated</span>
                    : <span data-testid={`user-status-${u.id}`} className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/15 text-emerald-500">Active</span>}
                </td>
                <td className="pr-4">
                  <div className="flex justify-end items-center gap-1">
                    {isSuper && (
                      <button onClick={() => setPermDlg(u)} data-testid={`perms-${u.id}`} className="p-1.5 rounded hover:bg-muted text-primary" title="Edit permissions">
                        <ShieldAlert className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="p-1.5 rounded hover:bg-muted text-primary" title="Edit user">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`} className="p-1.5 rounded hover:bg-muted" title={u.is_active === false ? "Activate" : "Deactivate"}>
                      {u.is_active === false ? <UserCheck className="w-3.5 h-3.5 text-emerald-500" /> : <UserX className="w-3.5 h-3.5 text-amber-500" />}
                    </button>
                    {isSuper && u.id !== currentUser?.id && (
                      <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} className="p-1.5 rounded hover:bg-muted text-destructive" title="Delete user">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit User" : "Create User"}</DialogTitle>
            <DialogDescription>{editId ? "Update this user's profile and role. Leave password blank to keep the existing one." : "New users are activated immediately and can log in with the initial password."}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" /></div>
            <div><Label>Mobile</Label><Input data-testid="form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Employee ID</Label><Input data-testid="form-empcode" value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} /></div>
            <div><Label>Department</Label><Input data-testid="form-dept" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
            <div><Label>Designation</Label><Input data-testid="form-desig" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="form-role"><SelectValue /></SelectTrigger>
                <SelectContent>{(schema.roles || []).map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g," ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {isSuper && !editId && (
              <div>
                <Label>Access template (optional)</Label>
                <Select value={form.template_id || "none"} onValueChange={(v) => setForm({ ...form, template_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="form-template"><SelectValue placeholder="Use role defaults" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Use role defaults</SelectItem>
                    {(templates || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{editId ? "New password (optional)" : "Initial password"}</Label>
              <Input data-testid="form-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editId ? "Leave blank to keep current" : "Set now"} />
            </div>
            <div className="col-span-2 flex items-center justify-between rounded-lg border border-border/60 px-3 py-2">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-muted-foreground">Deactivated users cannot log in.</div>
              </div>
              <Switch checked={form.is_active !== false} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="form-active" />
            </div>
          </div>
          <Button onClick={save} data-testid="save-user-btn" className="bg-primary text-primary-foreground">{editId ? "Update User" : "Create User"}</Button>
        </DialogContent>
      </Dialog>

      {/* Permission matrix dialog */}
      <PermissionMatrixDialog
        userDoc={permDlg}
        schema={schema}
        templates={templates}
        onTemplatesChanged={load}
        onClose={() => setPermDlg(null)}
        onSaved={() => { setPermDlg(null); load(); }}
      />
    </div>
  );
}

// ---------------- Permission matrix (per user) ----------------
function PermissionMatrixDialog({ userDoc, schema, templates, onTemplatesChanged, onClose, onSaved }) {
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!userDoc) return;
    setMatrix(JSON.parse(JSON.stringify(userDoc.permissions || {})));
  }, [userDoc]);

  const modules = schema.modules || [];
  const actions = schema.actions || [];

  const setCell = (mkey, action, val) => {
    setMatrix((m) => ({ ...m, [mkey]: { ...(m[mkey] || {}), [action]: val } }));
  };

  const resetFromDefault = () => {
    if (!userDoc) return;
    const def = (schema.role_defaults || {})[userDoc.role] || {};
    setMatrix(JSON.parse(JSON.stringify(def)));
  };

  const grantAll = () => {
    const all = {};
    for (const m of modules) {
      all[m.key] = {}; for (const a of actions) all[m.key][a] = true;
    }
    setMatrix(all);
  };

  const revokeAll = () => {
    const none = {};
    for (const m of modules) { none[m.key] = {}; for (const a of actions) none[m.key][a] = false; }
    setMatrix(none);
  };

  const applyTemplate = async (tid) => {
    const t = (templates || []).find((x) => x.id === tid);
    if (!t) return;
    // Update the visible matrix immediately, and persist to this user in one click.
    setMatrix(JSON.parse(JSON.stringify(t.permissions || {})));
    if (userDoc) {
      try {
        await api.post(`/users/${userDoc.id}/apply-template/${tid}`);
        toast.success(`Applied template "${t.name}"`);
        onTemplatesChanged && onTemplatesChanged();
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply template"); }
    }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Name this access template (e.g. 'Site Team')");
    if (!name || !name.trim()) return;
    try {
      await api.post("/permission-templates", { name: name.trim(), permissions: matrix });
      toast.success(`Template "${name.trim()}" saved`);
      onTemplatesChanged && onTemplatesChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save template"); }
  };

  const deleteTemplate = async (tid, ev) => {
    ev?.stopPropagation?.();
    if (!window.confirm("Delete this access template? Users already assigned keep their permissions.")) return;
    try {
      await api.delete(`/permission-templates/${tid}`);
      toast.success("Template deleted");
      onTemplatesChanged && onTemplatesChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete template"); }
  };

  const save = async () => {
    if (!userDoc) return;
    setSaving(true);
    try {
      await api.patch(`/users/${userDoc.id}/permissions`, { permissions: matrix });
      toast.success("Permissions updated");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const counts = useMemo(() => {
    let granted = 0, total = 0;
    for (const m of modules) {
      for (const a of actions) { total++; if (matrix?.[m.key]?.[a]) granted++; }
    }
    return { granted, total };
  }, [matrix, modules, actions]);

  return (
    <Dialog open={!!userDoc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-primary" /> Permissions · {userDoc?.name}</DialogTitle>
          <DialogDescription>Toggle exactly which modules and actions this user can perform. Hidden modules disappear from their sidebar and search.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between text-xs">
          <div className="text-muted-foreground">{counts.granted}/{counts.total} permissions granted</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={resetFromDefault} data-testid="perm-reset-btn">Reset to role default</Button>
            <Button size="sm" variant="outline" onClick={grantAll} data-testid="perm-grant-all">Grant all</Button>
            <Button size="sm" variant="outline" onClick={revokeAll} data-testid="perm-revoke-all">Revoke all</Button>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap rounded-lg border border-border/50 bg-muted/20 px-3 py-2" data-testid="perm-templates-bar">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Access template</span>
            <Select onValueChange={applyTemplate}>
              <SelectTrigger className="h-8 w-56" data-testid="perm-apply-template"><SelectValue placeholder="Apply a saved preset…" /></SelectTrigger>
              <SelectContent>
                {(templates || []).length === 0
                  ? <div className="px-3 py-2 text-xs text-muted-foreground">No templates yet — save one first</div>
                  : (templates || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" variant="outline" onClick={saveAsTemplate} data-testid="perm-save-template">Save current as template</Button>
        </div>
        {(templates || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="perm-template-chips">
            {templates.map((t) => (
              <span key={t.id} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border border-border/60 bg-card">
                {t.name}
                <button onClick={(e) => deleteTemplate(t.id, e)} data-testid={`perm-template-del-${t.id}`} className="text-destructive hover:opacity-70" title="Delete template">
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <tr>
                <th className="text-left py-2.5 px-3">Module</th>
                {actions.map((a) => <th key={a} className="text-center px-2 capitalize">{a}</th>)}
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m.key} data-testid={`perm-row-${m.key}`} className="border-t border-border/50">
                  <td className="py-2 px-3 font-medium">{m.label}<div className="text-[10px] text-muted-foreground font-mono">{m.key}</div></td>
                  {actions.map((a) => (
                    <td key={a} className="text-center">
                      <input
                        type="checkbox"
                        data-testid={`perm-${m.key}-${a}`}
                        checked={!!(matrix?.[m.key]?.[a])}
                        onChange={(e) => setCell(m.key, a, e.target.checked)}
                        className="w-4 h-4 rounded border-border cursor-pointer accent-primary"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="perm-save-btn" className="bg-primary text-primary-foreground">
            {saving ? "Saving…" : <><KeyRound className="w-4 h-4 mr-1.5" />Save Permissions</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
